import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { tasksAPI, calendarAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import {
    Loader2, CalendarClock, CheckSquare, Trash2, Lock, MapPin, Clock,
    User, AlertTriangle, ExternalLink, Pencil, X, Check, RotateCcw, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { MODULE_LABELS as CENTRAL_MODULE_LABELS } from '../../lib/moduleLabels';

const STATUS_LABELS = { todo: 'À faire', in_progress: 'À faire', postponed: 'Prolongée 📅', done: 'Terminée ✅' };
const PRIORITY_LABELS = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };
const PRIORITY_COLORS = {
    low: 'bg-slate-100 text-slate-700 border-slate-200',
    medium: 'bg-blue-50 text-blue-700 border-blue-200',
    high: 'bg-orange-50 text-orange-700 border-orange-200',
    urgent: 'bg-red-50 text-red-700 border-red-200',
};
// Utilise les labels canoniques (Courtage / Travaux d'optimisation) + fallback local
const MODULE_LABELS = { ...CENTRAL_MODULE_LABELS, both: 'Tout le dossier' };

function fmt(dt) {
    if (!dt) return '—';
    try {
        const d = new Date(dt);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) +
            (String(dt).length > 10 ? ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
    } catch { return String(dt).slice(0, 16); }
}

function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function toDateInput(dt) {
    if (!dt) return '';
    try { return new Date(dt).toISOString().slice(0, 10); } catch { return ''; }
}

export function WallItemDialog({ open, refType, refId, onClose, onChanged }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [item, setItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [postMode, setPostMode] = useState(false);
    const [reason, setReason] = useState('');
    const [newDate, setNewDate] = useState('');

    // Édition inline (canManage uniquement)
    const [editTitle, setEditTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [editDesc, setEditDesc] = useState(false);
    const [descDraft, setDescDraft] = useState('');
    const [editDue, setEditDue] = useState(false);
    const [dueDraft, setDueDraft] = useState('');

    const isEvent = refType === 'event';

    const load = useCallback(() => {
        if (!open || !refId) return;
        setLoading(true); setItem(null);
        setPostMode(false); setReason(''); setNewDate('');
        setEditTitle(false); setEditDesc(false); setEditDue(false);
        const req = isEvent ? calendarAPI.getEvent(refId) : tasksAPI.getById(refId);
        req.then(r => setItem(r.data))
            .catch(() => toast.error('Élément introuvable (peut-être supprimé).'))
            .finally(() => setLoading(false));
    }, [open, refId, isEvent]);

    useEffect(() => { load(); }, [load]);

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const canManage = !!item && (
        isAdmin ||
        item.created_by === user?.id ||
        item.assigned_to === user?.id ||
        (item.assignee_ids || []).includes(user?.id) ||
        (item.participant_ids || []).includes(user?.id)
    );

    const refresh = () => { load(); onChanged?.(); };

    const patchTask = async (payload, successMsg = 'Tâche mise à jour') => {
        try {
            setSaving(true);
            await tasksAPI.update(refId, payload);
            toast.success(successMsg);
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    const patchEvent = async (payload, successMsg = 'Événement mis à jour') => {
        try {
            setSaving(true);
            await calendarAPI.updateEvent(refId, payload);
            toast.success(successMsg);
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    const setTaskStatus = async (status) => {
        if (status === 'postponed') { setPostMode(true); return; }
        await patchTask({ status });
    };

    const submitPostpone = async () => {
        if (!reason.trim()) { toast.error('Indiquez une raison'); return; }
        if (!newDate) { toast.error('Choisissez une nouvelle date plus lointaine'); return; }
        await patchTask({ status: 'postponed', postpone_reason: reason.trim(), due_date: newDate }, 'Tâche prolongée');
        setPostMode(false);
    };

    const saveTitle = async () => {
        const val = titleDraft.trim();
        if (!val) { toast.error('Titre requis'); return; }
        if (val === item.title) { setEditTitle(false); return; }
        if (isEvent) await patchEvent({ title: val }); else await patchTask({ title: val });
        setEditTitle(false);
    };

    const saveDesc = async () => {
        if ((descDraft || '') === (item.description || '')) { setEditDesc(false); return; }
        if (isEvent) await patchEvent({ description: descDraft }); else await patchTask({ description: descDraft });
        setEditDesc(false);
    };

    const saveDue = async () => {
        if (!dueDraft) { toast.error('Date requise'); return; }
        if (isEvent) await patchEvent({ start_datetime: dueDraft }); else await patchTask({ due_date: dueDraft });
        setEditDue(false);
    };

    const changePriority = async (priority) => {
        await patchTask({ priority });
    };

    const remove = async () => {
        if (!window.confirm('Supprimer définitivement cet élément ?')) return;
        try {
            setSaving(true);
            if (isEvent) await calendarAPI.deleteEvent(refId); else await tasksAPI.delete(refId);
            toast.success('Supprimé'); onChanged?.(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const cancelEvent = async () => {
        await patchEvent({ status: 'cancelled' }, 'Événement annulé');
    };

    const openInFullPage = () => {
        onClose();
        if (isEvent) navigate(`/calendrier?event=${refId}`);
        else navigate(`/tasks?highlight=${refId}`);
    };

    // Liste des assignés (avatars + noms) — reconstruite depuis les données backend
    const assigneeList = [];
    if (item) {
        const seen = new Set();
        (item.assignee_ids || []).forEach((aid, i) => {
            if (aid && !seen.has(aid)) {
                seen.add(aid);
                assigneeList.push({ id: aid, name: item.assignee_names?.[i] || item.assigned_to_name || 'Utilisateur' });
            }
        });
        if (item.assigned_to && !seen.has(item.assigned_to)) {
            seen.add(item.assigned_to);
            assigneeList.push({ id: item.assigned_to, name: item.assigned_to_name || 'Utilisateur' });
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wall-item-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 pr-6">
                        {isEvent ? <CalendarClock className="w-5 h-5 text-blue-600" /> : <CheckSquare className="w-5 h-5 text-amber-600" />}
                        {isEvent ? 'Événement' : 'Tâche'}
                        {item?.module && MODULE_LABELS[item.module] && (
                            <Badge variant="outline" className="ml-1 text-[10px]">{MODULE_LABELS[item.module]}</Badge>
                        )}
                        {!canManage && item && (
                            <Badge variant="outline" className="ml-auto gap-1 text-slate-500"><Lock className="w-3 h-3" />Consultation</Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : !item ? (
                    <p className="py-8 text-center text-sm text-slate-500">Élément introuvable.</p>
                ) : (
                    <div className="space-y-4 text-sm">
                        {/* Titre — éditable inline pour canManage */}
                        <div>
                            {!editTitle ? (
                                <div className="flex items-start gap-2">
                                    <h3 className="text-base font-semibold text-slate-900 flex-1" data-testid="wall-item-title">{item.title}</h3>
                                    {canManage && (
                                        <button
                                            type="button"
                                            onClick={() => { setTitleDraft(item.title || ''); setEditTitle(true); }}
                                            className="shrink-0 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                                            title="Modifier le titre"
                                            data-testid="wall-item-edit-title"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    <Input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} className="text-sm" autoFocus data-testid="wall-item-title-input" />
                                    <div className="flex gap-1.5 justify-end">
                                        <Button size="sm" variant="outline" onClick={() => setEditTitle(false)}><X className="w-3.5 h-3.5" /></Button>
                                        <Button size="sm" onClick={saveTitle} disabled={saving} data-testid="wall-item-title-save"><Check className="w-3.5 h-3.5" /></Button>
                                    </div>
                                </div>
                            )}

                            {/* Description — éditable inline */}
                            {!editDesc ? (
                                <div className="flex items-start gap-2 mt-1">
                                    <p className="text-slate-600 whitespace-pre-wrap text-sm flex-1">
                                        {item.description || <span className="italic text-slate-400">Aucune description</span>}
                                    </p>
                                    {canManage && (
                                        <button
                                            type="button"
                                            onClick={() => { setDescDraft(item.description || ''); setEditDesc(true); }}
                                            className="shrink-0 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                                            title="Modifier la description"
                                            data-testid="wall-item-edit-desc"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-1.5 mt-1">
                                    <Textarea rows={3} value={descDraft} onChange={(e) => setDescDraft(e.target.value)} className="text-sm" data-testid="wall-item-desc-input" />
                                    <div className="flex gap-1.5 justify-end">
                                        <Button size="sm" variant="outline" onClick={() => setEditDesc(false)}><X className="w-3.5 h-3.5" /></Button>
                                        <Button size="sm" onClick={saveDesc} disabled={saving} data-testid="wall-item-desc-save"><Check className="w-3.5 h-3.5" /></Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Badges statut / priorité / date */}
                        <div className="flex flex-wrap items-center gap-2">
                            {isEvent ? (
                                <>
                                    <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{fmt(item.start_datetime)}</Badge>
                                    {item.status === 'cancelled' && <Badge className="bg-red-100 text-red-700">Annulé</Badge>}
                                    {item.location && <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" />{item.location}</Badge>}
                                </>
                            ) : (
                                <>
                                    <Badge className="bg-amber-100 text-amber-700">{STATUS_LABELS[item.status] || item.status}</Badge>
                                    {item.priority && (
                                        <Badge variant="outline" className={PRIORITY_COLORS[item.priority] || ''}>
                                            {PRIORITY_LABELS[item.priority] || item.priority}
                                        </Badge>
                                    )}
                                    {!editDue ? (
                                        <button
                                            type="button"
                                            onClick={() => canManage && (setDueDraft(toDateInput(item.due_date)), setEditDue(true))}
                                            disabled={!canManage}
                                            className={`inline-flex items-center gap-1 text-xs rounded-full border px-2 py-0.5 ${canManage ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'} border-slate-200 text-slate-600`}
                                            data-testid="wall-item-due"
                                        >
                                            <Clock className="w-3 h-3" />
                                            Éch. {fmt(item.due_date)}
                                            {canManage && <Pencil className="w-3 h-3 ml-0.5 opacity-50" />}
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-1">
                                            <Input type="date" value={dueDraft} onChange={(e) => setDueDraft(e.target.value)} className="h-7 text-xs w-36" data-testid="wall-item-due-input" />
                                            <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setEditDue(false)}><X className="w-3 h-3" /></Button>
                                            <Button size="sm" className="h-7 px-1.5" onClick={saveDue} disabled={saving} data-testid="wall-item-due-save"><Check className="w-3 h-3" /></Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {!isEvent && item.status === 'postponed' && item.postpone_reason && (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-800 text-xs flex gap-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <span>Prolongée — {item.postpone_reason}</span>
                            </div>
                        )}

                        {/* Assignés (avatars + noms) */}
                        {assigneeList.length > 0 && (
                            <div className="flex items-start gap-2 text-slate-600">
                                <Users className="w-4 h-4 shrink-0 mt-0.5" />
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {assigneeList.map(a => (
                                        <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-1 pr-2 py-0.5 text-xs" data-testid={`wall-item-assignee-${a.id}`}>
                                            <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-semibold">
                                                {initials(a.name)}
                                            </span>
                                            {a.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Créateur + date de création */}
                        {(item.created_by_name || item.created_at) && (
                            <div className="text-xs text-slate-400 flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" />
                                {item.created_by_name && <span>Créée par <b className="text-slate-500 font-medium">{item.created_by_name}</b></span>}
                                {item.created_at && <span>· {fmt(item.created_at)}</span>}
                            </div>
                        )}

                        {/* ===== Gestion (créateur / assigné / admin) ===== */}
                        {canManage && !postMode && (
                            <div className="border-t pt-3 space-y-3">
                                {!isEvent ? (
                                    <>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1.5">
                                                <Label className="text-xs">Statut</Label>
                                                <Select value={item.status === 'in_progress' ? 'todo' : item.status} onValueChange={setTaskStatus}>
                                                    <SelectTrigger data-testid="wall-item-status" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="todo">À faire</SelectItem>
                                                        <SelectItem value="postponed">Prolongée 📅</SelectItem>
                                                        <SelectItem value="done">Terminée ✅</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label className="text-xs">Priorité</Label>
                                                <Select value={item.priority || 'medium'} onValueChange={changePriority}>
                                                    <SelectTrigger data-testid="wall-item-priority" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="low">Basse</SelectItem>
                                                        <SelectItem value="medium">Moyenne</SelectItem>
                                                        <SelectItem value="high">Haute</SelectItem>
                                                        <SelectItem value="urgent">Urgente</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        {item.status === 'done' && (
                                            <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setTaskStatus('todo')} disabled={saving} data-testid="wall-item-reopen">
                                                <RotateCcw className="w-3.5 h-3.5" /> Rouvrir la tâche
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    item.status !== 'cancelled' && (
                                        <Button variant="outline" className="w-full text-red-600 border-red-200" onClick={cancelEvent} disabled={saving} data-testid="wall-item-cancel-event">
                                            Annuler l&apos;événement
                                        </Button>
                                    )
                                )}
                                <div className="flex gap-1.5">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 gap-1.5 text-slate-600"
                                        onClick={openInFullPage}
                                        data-testid="wall-item-open-full"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        {isEvent ? 'Ouvrir dans Calendrier' : 'Ouvrir dans Pilotage'}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 gap-1.5" onClick={remove} disabled={saving} data-testid="wall-item-delete">
                                        <Trash2 className="w-4 h-4" /> Supprimer
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* En lecture seule : bouton "Ouvrir dans Pilotage" quand même dispo */}
                        {!canManage && (
                            <div className="border-t pt-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full gap-1.5 text-slate-600"
                                    onClick={openInFullPage}
                                    data-testid="wall-item-open-full-readonly"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    {isEvent ? 'Voir dans Calendrier' : 'Voir dans Pilotage'}
                                </Button>
                            </div>
                        )}

                        {/* ===== Prolongation (raison + nouvelle date) ===== */}
                        {canManage && postMode && !isEvent && (
                            <div className="border-t pt-3 space-y-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Raison du prolongement <span className="text-red-500">*</span></Label>
                                    <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : client absent…" data-testid="wall-item-postpone-reason" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Nouvelle date (plus lointaine) <span className="text-red-500">*</span></Label>
                                    <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                                        min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} data-testid="wall-item-postpone-date" />
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setPostMode(false)}>Retour</Button>
                                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitPostpone} disabled={saving} data-testid="wall-item-postpone-confirm">Prolonger</Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
